/** Autosave and the undo journal, in the browser's own private filesystem — phase 3.
 *
 * ⚠⚠ **THE ORIGIN PRIVATE FILE SYSTEM, NOT localStorage AND NOT IndexedDB.** A client drawing's
 * rows are 19 MB of JSON and `localStorage` is a 5 MB synchronous string store — it would block
 * the main thread and then throw. OPFS is the one browser store that takes a file-sized write
 * without a schema, and `createWritable` replaces the file ATOMICALLY: the old contents stay
 * readable until `close()`, so a tab killed mid-save leaves the PREVIOUS autosave intact rather
 * than half of this one.
 *
 * ⚠⚠ **OPFS IS NOT THE DRAWING. IT IS A CRASH NET.** The drawing lives in the file the drafter
 * opened (`files.ts`) or on the server; this is what a reload finds when the tab died before a
 * save. Treating it as the document would invent a second source of truth for the same drawing —
 * the defect this app has shipped in other shapes and recorded at length.
 *
 * ⚠ Everything here ANSWERS rather than throws when OPFS is missing: Firefox shipped it in 111
 * and Safari in 15.2, but a WebView or a private window can still say no, and a drafter with no
 * autosave should get a drawing, not an error screen.
 */

/** One drawing's autosave: the rows the engine was opened with, and the journal since. */
export interface Autosave {
  /** ⚠ The drawing's own id when it came from the server, or the file's name for a local one —
   *  the key has to be stable across reloads and unique per drawing. */
  key: string;
  /** What it is called on screen. */
  name: string;
  /** `display_rs.store_input`'s JSON — exactly what `store.open` takes. */
  rows: string;
  /** Milliseconds since the epoch, from the writer. */
  savedAt: number;
  /** How many journal entries were folded in when this was written. */
  journalAt: number;
}

/** One edit, as `POST /edit/` states it, plus the inverse the engine handed back. ⚠ The SAME
 *  shape the server journals in `DrawingEdit`, so a local drawing that later goes to the server
 *  replays without a translation nobody can check. */
export interface JournalEntry {
  op: string;
  handles: string[];
  params: unknown;
  /** The engine's `inverse` — the old tags, never an inverse transform. `edits.do_transform`
   *  says why: rotating by -θ after +θ moves a HATCH's pattern origin. */
  inverse: unknown;
  at: number;
}

const ROOT = "pdcad";
const ROWS = "rows.json";
const JOURNAL = "journal.jsonl";
const META = "meta.json";

/** Is OPFS usable here? ⚠ `navigator.storage` exists in more places than `getDirectory` does. */
export function opfsAvailable(): boolean {
  return typeof navigator !== "undefined"
    && typeof navigator.storage?.getDirectory === "function";
}

async function dirFor(key: string, create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (!opfsAvailable()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const mine = await root.getDirectoryHandle(ROOT, { create });
    // ⚠ The key is a directory NAME, so it must survive being one: a drawing called
    // `plans/level 2.dwg` would otherwise try to make a nested directory that is not there.
    return await mine.getDirectoryHandle(safeKey(key), { create });
  } catch {
    return null;
  }
}

/** A key that is legal as a directory name, and still tells two drawings apart.
 *
 * ⚠ Not a hash: a drafter who has to be told which autosave is which needs to be able to READ
 * the name, and `find`ing one by eye in devtools is how the first bug here will be found. Every
 * character OPFS might refuse becomes `_`, and the original is kept in `meta.json`. */
export function safeKey(key: string): string {
  const s = key.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
  return s.length ? s.slice(0, 120) : "drawing";
}

async function writeFile(dir: FileSystemDirectoryHandle, name: string,
                         text: string): Promise<void> {
  const fh = await dir.getFileHandle(name, { create: true });
  // ⚠ `createWritable` writes to a SHADOW copy and swaps on close — that is what makes a killed
  // tab leave the previous version rather than a truncated one.
  const w = await fh.createWritable();
  try {
    await w.write(text);
  } finally {
    await w.close();
  }
}

async function readFile(dir: FileSystemDirectoryHandle, name: string): Promise<string | null> {
  try {
    const fh = await dir.getFileHandle(name);
    return await (await fh.getFile()).text();
  } catch {
    return null;
  }
}

/** Write the whole drawing. ⚠ Called on OPEN and after a fold, not per edit: 19 MB per keystroke
 *  would be the slowest thing in the app. Between folds the JOURNAL is what protects the work. */
export async function saveRows(key: string, name: string, rows: string,
                               journalAt = 0): Promise<boolean> {
  const dir = await dirFor(key, true);
  if (!dir) return false;
  try {
    await writeFile(dir, ROWS, rows);
    const meta: Omit<Autosave, "rows"> = { key, name, savedAt: Date.now(), journalAt };
    await writeFile(dir, META, JSON.stringify(meta));
    return true;
  } catch {
    return false;
  }
}

/** Everything a reload needs, or `null` when there is no autosave for this drawing. */
export async function loadAutosave(key: string): Promise<Autosave | null> {
  const dir = await dirFor(key, false);
  if (!dir) return null;
  const [rows, metaText] = await Promise.all([readFile(dir, ROWS), readFile(dir, META)]);
  if (rows === null) return null;
  let meta: Partial<Autosave> = {};
  try {
    meta = metaText ? JSON.parse(metaText) : {};
  } catch {
    meta = {};
  }
  return { key, name: meta.name ?? key, rows, savedAt: meta.savedAt ?? 0,
           journalAt: meta.journalAt ?? 0 };
}

/** Append one edit.
 *
 * ⚠⚠ **APPENDED, NOT REWRITTEN — WHICH IS WHY THE JOURNAL IS JSONL AND NOT A JSON ARRAY.** A
 * thousand-edit session rewriting one array is a thousand full writes; `keepExistingData` plus a
 * seek to the end is one write of one line. The format is the cost: a line, not a document.
 *
 * ⚠ A failed append is reported, never swallowed. An autosave a drafter believes in and that is
 * not there is worse than one they know they do not have.
 */
export async function appendJournal(key: string, entry: JournalEntry): Promise<boolean> {
  const dir = await dirFor(key, true);
  if (!dir) return false;
  try {
    const fh = await dir.getFileHandle(JOURNAL, { create: true });
    const size = (await fh.getFile()).size;
    const w = await fh.createWritable({ keepExistingData: true });
    try {
      await w.seek(size);
      await w.write(JSON.stringify(entry) + "\n");
    } finally {
      await w.close();
    }
    return true;
  } catch {
    return false;
  }
}

/** The journal, in the order it was written. ⚠ A truncated last line is DROPPED rather than
 *  failing the read: that line is exactly what a tab killed mid-write leaves behind, and losing
 *  one edit is the point of the exercise — losing the file is not. */
export async function readJournal(key: string): Promise<JournalEntry[]> {
  const dir = await dirFor(key, false);
  if (!dir) return [];
  const text = await readFile(dir, JOURNAL);
  if (!text) return [];
  const out: JournalEntry[] = [];
  for (const line of text.split("\n")) {
    if (!line.trim()) continue;
    try {
      out.push(JSON.parse(line) as JournalEntry);
    } catch {
      /* the last line of a killed write */
    }
  }
  return out;
}

/** Start the journal again — after a fold into `rows.json`, or after a save to disk. */
export async function clearJournal(key: string): Promise<void> {
  const dir = await dirFor(key, false);
  if (!dir) return;
  try {
    await dir.removeEntry(JOURNAL);
  } catch {
    /* there was none */
  }
}

/** Forget a drawing entirely — the drafter saved it, or threw it away. */
export async function forget(key: string): Promise<void> {
  if (!opfsAvailable()) return;
  try {
    const root = await navigator.storage.getDirectory();
    const mine = await root.getDirectoryHandle(ROOT, { create: false });
    await mine.removeEntry(safeKey(key), { recursive: true });
  } catch {
    /* nothing stored */
  }
}

/** Every autosave this origin holds, newest first — what a "recover your work" list shows. */
export async function listAutosaves(): Promise<Omit<Autosave, "rows">[]> {
  if (!opfsAvailable()) return [];
  const out: Omit<Autosave, "rows">[] = [];
  try {
    const root = await navigator.storage.getDirectory();
    const mine = await root.getDirectoryHandle(ROOT, { create: false });
    // ⚠ `values()` is an async iterator and TypeScript's DOM lib does not declare it on
    // `FileSystemDirectoryHandle` in every version — narrowed here rather than cast at each use.
    const it = (mine as unknown as {
      values(): AsyncIterable<FileSystemHandle>;
    }).values();
    for await (const h of it) {
      if (h.kind !== "directory") continue;
      const dir = await mine.getDirectoryHandle(h.name);
      const metaText = await readFile(dir, META);
      if (!metaText) continue;
      try {
        out.push(JSON.parse(metaText) as Omit<Autosave, "rows">);
      } catch {
        /* a meta file mid-write */
      }
    }
  } catch {
    return out;
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}
